/**
 * Simulation d'une nuit à Néopolis.
 *
 * L'étape « Tester » du chapitre 2 montre la rue de 18 h à 7 h et laisse voir,
 * heure par heure, ce que fait la solution construite par l'îlot. C'est là que
 * la différence entre « allumé toute la nuit » et « allumé quand il faut »
 * devient visible plutôt que théorique.
 *
 * La fonction est pure et déterministe : la même solution donne toujours la
 * même nuit, ce qui rend la comparaison entre îlots possible en fin de séance.
 */
import { CHAIN_ROLES } from './content-schema.js';
import type { Content } from './content-schema.js';
import type { ChainState, GameState } from './state.js';

/**
 * Le déroulé d'une nuit, heure par heure.
 *
 * Les passants et l'heure du coucher du soleil sont des paramètres d'animation,
 * pas du contenu pédagogique : ils vivent ici. S'ils devaient varier selon la
 * saison ou le quartier, leur place serait dans le JSON.
 */
export const HEURES_DE_LA_NUIT = [18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6, 7] as const;

/** Nombre de passants dans la rue, heure par heure. */
const PASSANTS: Readonly<Record<number, number>> = {
  18: 8,
  19: 6,
  20: 4,
  21: 2,
  22: 1,
  23: 1,
  0: 0,
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 1,
  6: 3,
  7: 6,
};

/** Il fait nuit de 20 h à 6 h ; à 18 h, 19 h et 7 h, il fait encore ou déjà jour. */
const estNuit = (heure: number): boolean => heure >= 20 || heure <= 6;

/** Niveau d'éclairage en veille, quand la solution sait baisser l'intensité. */

export interface MomentDeLaNuit {
  readonly heure: number;
  readonly nuit: boolean;
  readonly passants: number;
  /** Intensité du lampadaire, de 0 à 100. */
  readonly niveau: number;
}

export interface NuitSimulee {
  readonly moments: readonly MomentDeLaNuit[];
  /**
   * Consommation de la nuit, en « heures de pleine lumière ».
   * Un lampadaire allumé à fond pendant les 14 heures consommerait 14.
   */
  readonly consommation: number;
  /** Ce que la ville consommait avant, avec l'éclairage actuel. */
  readonly consommationActuelle: number;
}

/**
 * Comportement du lampadaire, déduit des équipements posés — pas de
 * l'identifiant de la solution.
 *
 * Une chaîne inédite est donc simulée elle aussi, ce qui permet à l'îlot de
 * tester une idée avant de savoir si elle correspond à une solution listée.
 */
function niveauPour(
  chain: ChainState,
  moment: { nuit: boolean; passants: number },
  content: Content,
): number {
  const equipements = new Set(CHAIN_ROLES.flatMap((role) => chain[role]));

  const lampe = equipements.has('lampadaire_led');
  if (!lampe) {
    return 0;
  }

  const saitSiIlFaitNuit = equipements.has('capteur_luminosite');
  const saitSiQuelquunPasse = equipements.has('detecteur_presence') || equipements.has('camera');
  const saitDecider = equipements.has('microcontroleur') || equipements.has('serveur');

  // Sans unité de traitement, la lampe reste sur son horloge : allumée du début
  // à la fin de la nuit, même quand il fait encore jour.
  if (!saitDecider) {
    return 100;
  }

  if (saitSiIlFaitNuit && saitSiQuelquunPasse) {
    if (!moment.nuit) return 0;
    return moment.passants > 0 ? 100 : content.reglages.niveau_veille_pourcent;
  }

  if (saitSiIlFaitNuit) {
    return moment.nuit ? 100 : 0;
  }

  if (saitSiQuelquunPasse) {
    // Personne ne lui dit qu'il fait jour : il s'allume au passage, midi compris.
    return moment.passants > 0 ? 100 : 0;
  }

  return 100;
}

/**
 * La nuit telle que Néopolis la vit aujourd'hui : lampadaire allumé en continu,
 * rue vide ou non. C'est ce que montre l'animation de l'étape « Comprendre ».
 */
export function simulateCurrentLighting(): NuitSimulee {
  const moments = HEURES_DE_LA_NUIT.map((heure) => ({
    heure,
    nuit: estNuit(heure),
    passants: PASSANTS[heure] ?? 0,
    niveau: 100,
  }));

  return {
    moments,
    consommation: HEURES_DE_LA_NUIT.length,
    consommationActuelle: HEURES_DE_LA_NUIT.length,
  };
}

/** Joue la nuit avec la chaîne construite par l'îlot. */
export function simulateNight(state: GameState, content: Content): NuitSimulee {
  const moments = HEURES_DE_LA_NUIT.map((heure) => {
    const contexte = { nuit: estNuit(heure), passants: PASSANTS[heure] ?? 0 };
    return {
      heure,
      nuit: contexte.nuit,
      passants: contexte.passants,
      niveau: niveauPour(state.chain, contexte, content),
    };
  });

  const consommation = moments.reduce((total, moment) => total + moment.niveau / 100, 0);

  // L'éclairage actuel de Néopolis : allumé à fond, du crépuscule à l'aube.
  const consommationActuelle = HEURES_DE_LA_NUIT.length;

  return { moments, consommation, consommationActuelle };
}
